import { useMemo } from 'react';
import { css, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import type { StackConfig } from 'common/stack/StackConfigDocuments';
import { StackFull } from 'client/components/Icons/StackFull';
import { Stack } from 'client/components/Stack';
import { Typography } from 'client/components/Typography';
import { StackPreviewComponent } from './StackPreviewComponent';

export interface StackPreviewProps {
  title: string;
  description: string;
  stackConfig?: Pick<StackConfig, 'components' | 'addons'> | null;
}

export const StackPreview = ({ title, description, stackConfig }: StackPreviewProps) => {
  const components = stackConfig?.components || [];
  const addons = stackConfig?.addons || [];

  const dependencies = useMemo(() => [...components, ...addons], [components, addons]);

  return (
    <Stack css={cardStyle}>
      <div css={headerStyle}>
        <Typography variant="h6">
          <StackFull weight="fill" size={24} /> {title}
        </Typography>
        <Typography variant="body2">{description}</Typography>
      </div>

      <StackPreviewComponent components={dependencies} />
    </Stack>
  );
};

const cardStyle = (theme: Theme) => css`
  padding: 26px;
  border-radius: ${theme.borderRadius}px;
  border: 2px solid ${theme.color.primary[100]};
  background: linear-gradient(
    -5deg,
    ${theme.color.background},
    ${transparentize(theme.type === 'light' ? 0.35 : 0, theme.color.primary[60])} 90%
  );
  box-sizing: border-box;
`;

const headerStyle = (theme: Theme) => css`
  > h6 {
    display: flex;
    align-items: center;
    gap: 8px;

    > svg {
      color: ${theme.color.primary[100]};
      translate: 0 -2px;
    }
  }
`;
